/**
 * back-nav.ts — 集中式返回堆疊(學習側統一導覽)。
 *
 * boss 2026-06-14:把學習側「各畫面回上頁方式不一致」統一成 iOS 風格 ——
 * 左上 ‹ 返回 + 左緣滑動 + 翻頁轉場,三者最後都呼這裡的 `goBack()`。
 *
 * 設計:純邏輯模組(可單測,零 DOM 依賴)。每個畫面 mount 時 `pushBackHandler`
 * 推一個返回處理器,unmount 時呼回傳的「取消註冊」函式把它移掉。`goBack()` 執行
 * **堆疊頂端**(= 當前可見畫面)的處理器;若該畫面註冊時帶 `confirm` 字串,先跳確認,
 * 取消則不返回(破壞性答題 / 考卷未存進度的畫面用)。
 *
 * 確認對話框用可注入的函式(`setConfirmFn`),預設綁瀏覽器 `window.confirm`;測試可
 * mock,純邏輯不碰真 DOM。
 *
 * ⚠️ 學習/遊戲分離:純導覽,不碰 factionXP / commanderXp / skillLevels / 戰鬥數值 /
 * 帳號。遊戲端(Phaser)不使用本模組。
 */

/** 單一返回項目。 */
interface BackEntry {
  /** 返回處理器(執行實際的「回上一頁」)。 */
  fn: () => void;
  /** 選填確認字串:有值時 goBack 先跳確認,取消則不執行 fn。 */
  confirm?: string;
}

/** 推返回處理器時的選項。 */
export interface PushBackOptions {
  /**
   * 選填確認字串。有值 → `goBack` 觸發此處理器時先跳該確認對話框,使用者取消則
   * 不返回(答題 / 考卷等未存進度畫面用,避免靜默丟失作答)。
   */
  confirm?: string;
}

/** 確認對話框函式型別(回傳 true = 確定離開)。 */
export type ConfirmFn = (message: string) => boolean;

/** 預設確認函式 —— 瀏覽器 `window.confirm`;SSR / 測試環境安全降級為 true。 */
const defaultConfirm: ConfirmFn = (message) => {
  if (typeof window !== 'undefined' && typeof window.confirm === 'function') {
    return window.confirm(message);
  }
  return true;
};

/** 返回堆疊(後進先出,頂端 = 當前可見畫面)。 */
const stack: BackEntry[] = [];

/** 目前的確認函式(可注入)。 */
let confirmFn: ConfirmFn = defaultConfirm;

/**
 * 推一個返回處理器,回傳「取消註冊」函式。
 *
 * 取消註冊只會移除**這一筆**(用 identity 比對),即使它不在堆疊頂端也安全 ——
 * 多畫面交錯 mount/unmount 不會誤刪別人的處理器。重複呼叫取消函式為 no-op。
 *
 * @param fn   返回處理器(回上一頁的實際動作)。
 * @param opts `confirm` 有值 → goBack 時先跳該確認字串,取消則不返回。
 * @returns    取消註冊函式(unmount 時呼叫)。
 */
export function pushBackHandler(fn: () => void, opts?: PushBackOptions): () => void {
  const entry: BackEntry = { fn, confirm: opts?.confirm };
  stack.push(entry);
  let removed = false;
  return () => {
    if (removed) return;
    removed = true;
    const idx = stack.indexOf(entry);
    if (idx !== -1) stack.splice(idx, 1);
  };
}

/**
 * 執行堆疊頂端的返回處理器。
 *
 * - 堆疊空 → 回 `false`(沒有可返回的畫面,呼叫端可忽略)。
 * - 頂端帶 `confirm` → 先跳確認;取消(confirm 回 false)→ 回 `false`、**不返回**、
 *   處理器留在堆疊上。
 * - 確認通過(或無 confirm)→ 執行 `fn`(由 `fn` 自己負責換頁 / unmount),回 `true`。
 *
 * 注意:`goBack` **不**自動把頂端 pop 掉 —— pop 由畫面 unmount 時的取消註冊函式負責,
 * 確保「堆疊深度 = 實際可見畫面數」這個不變式由 mount/unmount 生命週期維護,而非由
 * goBack 猜測。
 *
 * @returns 是否實際觸發了返回。
 */
export function goBack(): boolean {
  const top = stack[stack.length - 1];
  if (!top) return false;
  if (top.confirm && !confirmFn(top.confirm)) {
    return false;
  }
  top.fn();
  return true;
}

/** 目前堆疊深度(可見畫面層數)。 */
export function backStackDepth(): number {
  return stack.length;
}

/**
 * 清空整個返回堆疊。
 *
 * 給 app 層在「硬切畫面」(如 main.ts `setMode` 把 learnRoot innerHTML 清掉,
 * 舊畫面的 unmount 不會被呼到)時用 —— 清乾淨後讓新畫面重新 push 自己的處理器,
 * 維持「深度 = 可見畫面數」不變式。
 */
export function resetBackStack(): void {
  stack.length = 0;
}

/**
 * 注入確認對話框函式(預設 = 瀏覽器 `window.confirm`)。測試用來 mock 確認結果。
 * 傳 `null` / 不傳 → 還原成預設。
 */
export function setConfirmFn(fn?: ConfirmFn | null): void {
  confirmFn = fn ?? defaultConfirm;
}
